import { useState, useEffect, useRef, useMemo } from 'react';
import { usePricingData } from './usePricingData';
import { PricingForm } from './PricingForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Edit,
  Trash2,
  Plus,
  Filter,
  Search,
  Calculator,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import { ProductPrice } from '@/services/productService';
import { useQueryClient } from '@tanstack/react-query';
import {
  formatNumberForDisplay,
  formatDateForDisplay,
  type DateFormat,
} from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logDebug } from '@/utils/errorHandler';

interface PriceTabProps {
  productId: number;
  prices: ProductPrice[];
  isPricesLoading: boolean;
  onPricesUpdated: () => Promise<void>;
}

export default function PriceTab({
  productId,
  prices: _pricesFromProps,
  isPricesLoading: _isPricesLoading,
  onPricesUpdated,
}: PriceTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState<number | null>(null);

  const {
    prices: filteredPrices,
    rawPrices,
    error,
    summary,
    filters,
    setFilters,
    refresh,
    currencies,
    add,
    update,
    remove,
    isLoading: pricingDataLoading,
  } = usePricingData(productId);

  const { priceTypes: metaPriceTypes } = usePriceMetadata();

  // Use the filtered prices from usePricingData hook instead of props
  const prices = filteredPrices;
  const isLoading = pricingDataLoading;

  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus management for accessibility
  useEffect(() => {
    if (drawerOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [drawerOpen]);

  // Note: Currency filtering is handled in usePricingData hook

  // Show all available currencies, not just used ones
  const filteredCurrencies = useMemo(() => {
    const filtered = currencies.filter(c => c.code && c.code.trim() !== '');
    logDebug('Filtering currencies for price tab', {
      allCurrencies: currencies.length,
      filteredCurrencies: filtered.length,
    });
    return filtered;
  }, [currencies]);

  // Create a function to get price type label from metaPriceTypes
  const getPriceTypeDisplayLabel = (priceTypeCode: string): string => {
    const priceType = metaPriceTypes.find(pt => pt.code === priceTypeCode);
    return priceType ? priceType.label : priceTypeCode;
  };

  // Get organization settings for formatting
  const { orgSettings } = useOrgSettings();
  const numberFormat = orgSettings?.number_format || 'dot';
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Helper function to format currency respecting organization number format
  const formatCurrencyWithOrgFormat = (
    amount: number | string | null | undefined,
    currency: string
  ) => {
    // Convert to number and handle edge cases
    let numericAmount: number;

    if (typeof amount === 'number') {
      numericAmount = amount;
    } else if (typeof amount === 'string') {
      numericAmount = parseFloat(amount);
    } else {
      numericAmount = 0;
    }

    // Handle invalid numbers
    if (isNaN(numericAmount)) {
      numericAmount = 0;
    }

    // Always use organization-aware formatting
    const formattedNumber = formatNumberForDisplay(numericAmount, numberFormat, 2);

    if (numberFormat === 'comma') {
      return `${formattedNumber} ${currency}`;
    } else {
      return `${currency} ${formattedNumber}`;
    }
  };

  // Get translations
  const { getPriceTabLabels, getPriceTabNotification } = useProductDetailPageTranslations();
  const priceTabLabels = getPriceTabLabels;

  const handleAddPrice = () => {
    setSelectedPrice(null);
    setDrawerOpen(true);
  };

  const handleEditPrice = (price: any) => {
    // Use metaPriceTypes for code lookup
    const priceTypeObj = metaPriceTypes.find(pt => pt.code === price.priceType);
    const priceTypeId = priceTypeObj ? priceTypeObj.id.toString() : price.priceType;

    setSelectedPrice({
      id: price.id,
      priceType: priceTypeId,
      currencyCode: price.currencyCode,
      value: price.value,
      validFrom: price.validFrom || '',
      validTo: price.validTo || '',
    });
    setDrawerOpen(true);
  };

  const handleDeletePrice = async (id: number) => {
    try {
      await remove(id);
      toast({ title: getPriceTabNotification('priceDeleted') });

      // Invalidate and refetch product queries
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.refetchQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['prices', productId] });

      await onPricesUpdated();
    } catch (error) {
      toast({
        title: priceTabLabels.errors.deleteFailed,
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setPriceToDelete(null);
    }
  };

  const confirmDelete = (id: number) => {
    setPriceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedRows.map(id => remove(id)));
      toast({
        title: getPriceTabNotification('pricesDeleted', {
          count: selectedRows.length,
        }),
      });
      setSelectedRows([]);

      // Invalidate and refetch product queries
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.refetchQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['prices', productId] });

      await onPricesUpdated();
    } catch (error) {
      toast({
        title: priceTabLabels.errors.deleteMultipleFailed,
        variant: 'destructive',
      });
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.length === prices.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(prices.map(p => p.id));
    }
  };

  const toggleSelectRow = (id: number) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handlePriceUpdateSuccess = async () => {
    // Refresh the local state
    refresh();
    setDrawerOpen(false);
    setSelectedPrice(null);

    // Invalidate and refetch both product and prices queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['product', productId] }),
      queryClient.invalidateQueries({ queryKey: ['prices', productId] }),
    ]);

    // Force immediate refetch
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['product', productId] }),
      queryClient.refetchQueries({ queryKey: ['prices', productId] }),
    ]);
  };

  if (error) {
    return (
      <Card className="mb-6">
        <CardContent className="py-10 text-center">
          <div className="mb-2 text-destructive">{priceTabLabels.errors.loadFailed}</div>
          <Button onClick={refresh}>{priceTabLabels.actions.tryAgain}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="col-span-1 md:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center text-xl">
              <Calculator className="mr-2 size-5" />
              {priceTabLabels.summary.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-md bg-muted p-4">
                <div className="text-sm text-muted-foreground">
                  {priceTabLabels.summary.minPrice}
                </div>
                {isLoading ? (
                  <Skeleton className="mt-1 h-8 w-24" />
                ) : (
                  <div className="text-2xl font-semibold">
                    {summary.min !== null
                      ? formatCurrencyWithOrgFormat(summary.min, 'USD')
                      : priceTabLabels.summary.noData}
                  </div>
                )}
              </div>
              <div className="rounded-md bg-muted p-4">
                <div className="text-sm text-muted-foreground">
                  {priceTabLabels.summary.avgPrice}
                </div>
                {isLoading ? (
                  <Skeleton className="mt-1 h-8 w-24" />
                ) : (
                  <div className="text-2xl font-semibold">
                    {summary.avg !== null
                      ? formatCurrencyWithOrgFormat(summary.avg, 'USD')
                      : priceTabLabels.summary.noData}
                  </div>
                )}
              </div>
              <div className="rounded-md bg-muted p-4">
                <div className="text-sm text-muted-foreground">
                  {priceTabLabels.summary.maxPrice}
                </div>
                {isLoading ? (
                  <Skeleton className="mt-1 h-8 w-24" />
                ) : (
                  <div className="text-2xl font-semibold">
                    {summary.max !== null
                      ? formatCurrencyWithOrgFormat(summary.max, 'USD')
                      : priceTabLabels.summary.noData}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex h-full flex-col items-center justify-center p-6">
            <p className="mb-2 text-sm text-muted-foreground">
              {priceTabLabels.summary.totalPrices}
            </p>
            <div className="mb-4 text-3xl font-bold">{summary.total}</div>
            <Button onClick={handleAddPrice} className="w-full">
              <Plus className="mr-2 size-4" />
              {priceTabLabels.actions.addPrice}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder={priceTabLabels.filters.searchPlaceholder}
                className="pl-9"
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div className="md:w-1/4">
              <Select
                value={filters.currency}
                onValueChange={value => setFilters({ ...filters, currency: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={priceTabLabels.filters.allCurrencies} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{priceTabLabels.filters.allCurrencies}</SelectItem>
                  {filteredCurrencies
                    .map(c => {
                      // Ensure we have a valid code before rendering
                      if (!c.code || c.code.trim() === '') {
                        logDebug('Skipping currency with empty code', { currency: c });
                        return null;
                      }
                      return (
                        <SelectItem key={c.id || c.code} value={c.code}>
                          {c.code} ({c.symbol || c.name})
                        </SelectItem>
                      );
                    })
                    .filter(Boolean)}
                </SelectContent>
              </Select>
            </div>
            <div className="relative md:w-1/4">
              <Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
              <Input
                type="date"
                placeholder={priceTabLabels.filters.validToPlaceholder}
                className="pl-9"
                value={filters.validTo || ''}
                onChange={e => setFilters({ ...filters, validTo: e.target.value || null })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b p-4">
            <div className="flex items-center space-x-2">
              <Filter className="size-4 text-muted-foreground" />
              <span className="font-medium">{priceTabLabels.table.prices}</span>
              {selectedRows.length > 0 && (
                <Badge variant="secondary">
                  {priceTabLabels.table.selected.replace(
                    '{{count}}',
                    selectedRows.length.toString()
                  )}
                </Badge>
              )}
            </div>
            {selectedRows.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="mr-2 size-4" />
                {priceTabLabels.actions.deleteSelected}
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedRows.length === prices.length && prices.length > 0}
                      onCheckedChange={toggleSelectAll}
                      aria-label={priceTabLabels.table.selectAll}
                    />
                  </TableHead>
                  <TableHead className="w-[200px]">
                    {priceTabLabels.table.headers.priceType}
                  </TableHead>
                  <TableHead className="w-[120px]">
                    {priceTabLabels.table.headers.currency}
                  </TableHead>
                  <TableHead className="w-[140px] text-right">
                    {priceTabLabels.table.headers.value}
                  </TableHead>
                  <TableHead className="w-[140px]">
                    {priceTabLabels.table.headers.validFrom}
                  </TableHead>
                  <TableHead className="w-[140px]">
                    {priceTabLabels.table.headers.validTo}
                  </TableHead>
                  <TableHead className="w-[100px] text-center">
                    {priceTabLabels.table.headers.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton className="size-4" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-16" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : prices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <p className="mb-2 text-muted-foreground">{priceTabLabels.table.noData}</p>
                        <Button variant="outline" size="sm" onClick={handleAddPrice}>
                          <Plus className="mr-2 size-4" />
                          {priceTabLabels.actions.addFirstPrice}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  prices.map(price => (
                    <TableRow
                      key={price.id}
                      className={`group ${price.isExpired ? 'opacity-60' : ''}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.includes(price.id)}
                          onCheckedChange={() => toggleSelectRow(price.id)}
                          aria-label={priceTabLabels.table.selectPrice.replace(
                            '{{id}}',
                            price.id.toString()
                          )}
                        />
                      </TableCell>
                      <TableCell>{getPriceTypeDisplayLabel(price.priceType)}</TableCell>
                      <TableCell>{price.currencyCode}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrencyWithOrgFormat(price.value, price.currencyCode)}
                      </TableCell>
                      <TableCell>
                        {price.validFrom ? formatDateForDisplay(price.validFrom, dateFormat) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {price.validTo ? formatDateForDisplay(price.validTo, dateFormat) : '-'}
                          {price.isExpired && (
                            <div className="flex items-center gap-1">
                              <AlertCircle className="size-4 text-red-500" />
                              <Badge variant="destructive" className="text-xs">
                                {priceTabLabels.table.expired}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditPrice(price)}
                          >
                            <Edit className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => confirmDelete(price.id)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Price Drawer */}
      <Drawer
        open={drawerOpen}
        onOpenChange={open => {
          setDrawerOpen(open);
          if (!open) setSelectedPrice(null);
        }}
      >
        <DrawerContent className="mx-auto max-w-md">
          <DrawerHeader>
            <DrawerTitle>
              {selectedPrice ? priceTabLabels.drawer.editTitle : priceTabLabels.drawer.addTitle}
            </DrawerTitle>
            <DrawerDescription>
              {selectedPrice
                ? priceTabLabels.drawer.editDescription
                : priceTabLabels.drawer.addDescription}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 py-2">
            <PricingForm
              key={selectedPrice?.id ?? 'new-price'}
              productId={productId}
              initialData={selectedPrice || undefined}
              onAdd={add}
              onUpdate={update}
              onSuccess={handlePriceUpdateSuccess}
            />
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline">{priceTabLabels.drawer.cancel}</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{priceTabLabels.deleteDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {priceTabLabels.deleteDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{priceTabLabels.deleteDialog.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => priceToDelete && handleDeletePrice(priceToDelete)}
              className="bg-red-500 hover:bg-red-600"
            >
              {priceTabLabels.deleteDialog.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
